import { useTranslation } from 'react-i18next';
import { Megaphone, Wrench, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { localizedText, useAnnouncements, type Announcement } from '@/data/useAnnouncements';

/**
 * operator-panel-v2 T2.7: the operator's announcements at the top of the portal (`my_announcements`,
 * re-read on focus and every five minutes). One card per notice in the reader's language; a
 * `maintenance` notice is styled with the warning feedback token, the rest as plain cards. A
 * dismissible notice has a close button and stays hidden on this browser; a non-dismissible one stays
 * until its window ends. Renders nothing when there is nothing to show.
 */
export function AnnouncementBanner({ enabled = true }: { enabled?: boolean }) {
  const { t, i18n } = useTranslation();
  const { items, dismiss } = useAnnouncements(enabled);
  if (items.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-3" role="region" aria-label={t('announcements.label')}>
      {items.map((a) => (
        <AnnouncementCard key={a.id} row={a} language={i18n.resolvedLanguage} onDismiss={a.dismissible ? () => dismiss(a.id) : undefined} />
      ))}
    </div>
  );
}

function AnnouncementCard({ row, language, onDismiss }: { row: Announcement; language: string | undefined; onDismiss?: () => void }) {
  const { t } = useTranslation();
  const { title, body } = localizedText(row, language);
  const maintenance = row.kind === 'maintenance';
  const Icon = maintenance ? Wrench : Megaphone;
  return (
    <section
      aria-label={title}
      data-kind={row.kind}
      className={cn(
        'flex gap-3 rounded-md border px-4 py-3 shadow-card',
        maintenance ? 'border-warning border-l-4 bg-card' : 'border-hairline bg-card',
      )}
    >
      <Icon className={cn('mt-0.5 hidden size-5 shrink-0 sm:block', maintenance ? 'text-warning' : 'text-info')} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{t(`announcements.kind.${row.kind}`)}</p>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        <p className="whitespace-pre-line text-base text-ink-secondary">{body}</p>
      </div>
      {onDismiss && (
        <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={onDismiss} aria-label={t('announcements.dismiss')}>
          <X className="size-4" aria-hidden="true" />
        </Button>
      )}
    </section>
  );
}
